import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle, Shield } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const AdminForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetUrl, setResetUrl] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/password-reset/super-admin/request`,
        { email }
      );

      if (response.data.success) {
        setResetToken(response.data.reset_token);
        setResetUrl(window.location.origin + response.data.reset_url);
        toast.success("Password reset instructions generated!");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error("Failed to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("Copied to clipboard!");
        })
        .catch(() => {
          // Fallback for clipboard API failure
          copyFallback(text);
        });
    } else {
      copyFallback(text);
    }

    function copyFallback(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy");
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back to Login */}
        <Link
          to="/admin/login"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Super Admin Login
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Super Admin Password Reset
            </h1>
            <p className="text-gray-600">
              Enter your super admin email for password reset
            </p>
          </div>

          {!resetToken ? (
            // Request Form
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Super Admin Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="superadmin@dev.scan2qr.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Send Reset Instructions"}
              </button>
            </form>
          ) : (
            // Success Message with Token
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
                  <div className="flex-1">
                    <h3 className="text-green-900 font-semibold mb-1">
                      Reset Instructions Generated!
                    </h3>
                    <p className="text-green-700 text-sm">
                      In production, these would be sent to your email. For now,
                      use the link below:
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reset Password Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={resetUrl}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(resetUrl)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Token (for testing) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reset Token (Testing Only)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={resetToken}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(resetToken)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="text-center">
                <a
                  href={resetUrl}
                  className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Go to Reset Password
                </a>
              </div>

              <div className="text-center">
                <button
                  onClick={() => {
                    setResetToken(null);
                    setEmail("");
                  }}
                  className="text-gray-600 hover:text-gray-900 text-sm"
                >
                  Request another reset
                </button>
              </div>
            </div>
          )}

          {/* Helper Text */}
          <div className="mt-6 text-center text-sm text-gray-500">
            Remember your password?{" "}
            <Link
              to="/admin/login"
              className="text-red-600 hover:text-red-700 font-medium"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPasswordPage;
